<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from "vue";
import { storeToRefs } from "pinia";
import { useForecastStore } from "@/stores/forecast.store";
import CurrentConditionsStrip from "@/components/weather/CurrentConditionsStrip.vue";
import RadarMapCard from "@/components/weather/RadarMapCard.vue";
import HourlyChartCanvas from "@/components/weather/HourlyChartCanvas.vue";
import { useDisplayStore } from "@/stores/display.store";
import WeeklyForecast from "@/components/weather/WeeklyForecast.vue";
import ForecastSectionHeading from "@/components/weather/ForecastSectionHeading.vue";
import { Skeleton } from "@/components/ui/skeleton";
import CardErrorState from "@/components/ui/CardErrorState.vue";
import { LineChart, Clock3, CalendarDays } from "@lucide/vue";
import { useWeatherStore } from "@/stores/weather.store";
import { useWeatherSessionStore } from "@/stores/weatherSession.store";
import { useSettingsStore } from "@/stores/settings.store";

const weatherStore = useWeatherStore();
const session = useWeatherSessionStore();
const settingsStore = useSettingsStore();
const display = useDisplayStore();
const wallMode = computed(() => display.wallMode);

const sections = computed(() =>
  wallMode.value
    ? ["conditions", "weekly", "hourly"]
    : settingsStore.homeSectionOrder,
);

const currentWeather = computed(() => weatherStore.currentWeather);
const currentLocation = computed(() => session.activeLocation);

// Loading and error states from store
const isLoading = computed(() => weatherStore.isLoading);

const hasError = computed(
  () => !!weatherStore.error && !weatherStore.hasWeatherData,
);

const forecast = useForecastStore();

const { hourlyForecast, weeklyForecast, chartCurrentIndex } =
  storeToRefs(forecast);

// Selected day index for connecting weekly forecast to hourly chart
const selectedDayIndex = ref(0);
const isScrolling = ref(false);
const hourlyChartRef = ref<InstanceType<typeof HourlyChartCanvas> | null>(null);

// Release the selection lock after programmatic hourly scrolling.
let scrollRelease: ReturnType<typeof setTimeout> | undefined;

/** Select a daily forecast and align the hourly chart. */
function onDaySelected(dayIndex: number) {
  selectedDayIndex.value = dayIndex;
  isScrolling.value = true;
  hourlyChartRef.value?.scrollToDay(dayIndex);
  clearTimeout(scrollRelease);
  scrollRelease = setTimeout(() => {
    isScrolling.value = false;
  }, 500);
}

/** Keep the selected daily card aligned with hourly scrolling. */
function onDayInView(dayIndex: number) {
  if (!isScrolling.value) {
    selectedDayIndex.value = dayIndex;
  }
}

// The chart returns to Now when its data or units change; keep the day selection aligned.
watch(hourlyForecast, () => {
  selectedDayIndex.value = 0;
});

onUnmounted(() => clearTimeout(scrollRelease));
</script>

<template>
  <div
    :class="[
      'forecast-page animate-fade-in',
      wallMode
        ? 'grid min-h-0 grid-cols-[minmax(0,1fr)] grid-rows-[minmax(380px,auto)_auto_auto] gap-3 pb-0 short-wall:grid-rows-[minmax(320px,auto)_auto_auto] short-wall:gap-2 compact-wall:grid-rows-[minmax(240px,auto)_auto_auto]!'
        : 'space-y-5 pb-8',
    ]"
  >
    <template v-for="section in sections" :key="section">
      <!-- Current Weather + Radar Grid -->
      <section
        v-if="section === 'conditions'"
        class="weather-hero [--edge:var(--conditions-divider)] [--material-edge:var(--conditions-edge)] [--material-face:var(--conditions-face)]"
        :class="
          wallMode
            ? 'grid min-h-0 grid-cols-[minmax(0,2fr)_minmax(0,3.3fr)] gap-3 min-[768px]:max-[960px]:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]'
            : 'flex flex-col gap-0 rounded-shell border border-app-borderSubtle bg-app-conditions shadow-[0_2px_8px_#0000000a]'
        "
        aria-label="Current conditions and radar"
      >
        <CurrentConditionsStrip
          :class="
            wallMode
              ? 'rounded-panel border border-app-borderSubtle'
              : 'rounded-t-[var(--radius-shell-inner)] rounded-b-none'
          "
          :wall-mode="wallMode"
          :unit="settingsStore.temperatureUnit"
          :weather="currentWeather"
          :loading="isLoading"
          :error="hasError"
        />
        <div
          class="radar-surface overflow-hidden bg-app-material"
          :class="
            wallMode
              ? 'min-h-0 rounded-panel'
              : 'rounded-t-panel rounded-b-[var(--radius-shell-inner)]'
          "
        >
          <RadarMapCard
            class="hero-map rounded-none p-0 [&>div]:rounded-none"
            :class="
              wallMode
                ? 'h-full min-h-0 [&_.map-gesture-hint]:hidden'
                : 'h-[420px] min-h-[420px] max-md:h-[360px] max-md:min-h-[360px]'
            "
            :wall-mode="wallMode"
            :location="currentLocation"
            :loading="isLoading"
            :preferences="settingsStore.mapPreferences"
            @toggle-radar="
              settingsStore.showRadarLayer = !settingsStore.showRadarLayer
            "
            @toggle-wind="
              settingsStore.showWindLayer = !settingsStore.showWindLayer
            "
            @radar-opacity="settingsStore.setRadarOpacity"
            @wind-opacity="settingsStore.setWindParticleOpacity"
            @radar-source="settingsStore.setDevRadarSource"
          />
        </div>
      </section>

      <!-- Hourly Forecast Chart -->
      <section
        v-else-if="section === 'hourly'"
        class="hourly-section rounded-shell border border-app-borderSubtle bg-app-conditions px-3.5 pt-4 pb-3.5"
        :class="{ 'mt-3.5 min-w-0': wallMode }"
        aria-labelledby="hourly-heading"
      >
        <ForecastSectionHeading
          class="mx-2 mb-3.5"
          heading-id="hourly-heading"
          title="Hourly forecast"
          detail="Temperature, precipitation & wind"
          :icon="Clock3"
        />
        <p
          v-if="weatherStore.error && hourlyForecast.length"
          role="status"
          class="mx-2 mb-3 text-sm text-text-muted"
        >
          Couldn’t update the forecast. Showing the last available data.
        </p>
        <div
          class="hourly-surface overflow-hidden rounded-control border border-app-borderSubtle bg-app-material"
        >
          <!-- Loading State -->
          <div v-if="isLoading" class="relative h-[420px] overflow-hidden">
            <!-- Chart area with icons at varying heights -->
            <div class="absolute inset-x-0 top-[60px] bottom-[120px]">
              <div class="flex justify-between px-4">
                <div
                  v-for="i in 24"
                  :key="i"
                  class="flex flex-col items-center"
                >
                  <!-- Icon at varying height to simulate wave -->
                  <div
                    class="flex flex-col items-center gap-2"
                    :style="{
                      marginTop: `${40 + Math.sin(i * 0.5) * 60}px`,
                    }"
                  >
                    <Skeleton class="h-10 w-10 rounded-xl" />
                    <Skeleton class="h-[22px] w-9 rounded-full" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Baseline -->
            <div class="absolute inset-x-0 top-[300px] h-px bg-app-border" />

            <!-- Bottom label band (120px) -->
            <div class="absolute inset-x-0 bottom-0 h-[120px] pt-2">
              <div class="flex justify-between px-4">
                <div
                  v-for="i in 24"
                  :key="i"
                  class="flex flex-col items-center gap-1"
                >
                  <!-- Wind arrow -->
                  <Skeleton class="mt-1 h-4 w-4 rounded" />
                  <!-- Wind speed -->
                  <Skeleton class="h-3 w-5" />
                  <!-- Wind unit -->
                  <Skeleton class="h-2 w-6" />
                  <!-- Time labels -->
                  <div class="mt-3 flex flex-col items-center gap-1">
                    <Skeleton class="h-2.5 w-7" />
                    <Skeleton class="h-3 w-8" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Chart -->
          <HourlyChartCanvas
            v-else-if="hourlyForecast.length > 0"
            :ref="
              (instance) =>
                (hourlyChartRef = instance as InstanceType<
                  typeof HourlyChartCanvas
                > | null)
            "
            :unit="settingsStore.temperatureUnit"
            :dark="settingsStore.isDarkMode"
            :hours="hourlyForecast"
            :current-hour-index="chartCurrentIndex"
            @day-in-view="onDayInView"
          />

          <!-- Empty State -->
          <CardErrorState
            v-else
            class="min-h-[220px]"
            :icon="LineChart"
            message="Hourly forecast unavailable"
            description="Hourly predictions aren’t available for this location right now. Try refreshing later."
          />
        </div>
      </section>

      <!-- 8-Day Forecast -->
      <section
        v-else
        class="weekly-section min-h-0"
        aria-labelledby="weekly-heading"
      >
        <ForecastSectionHeading
          class="mx-0.5 mb-4"
          :class="{ 'sr-only': wallMode }"
          heading-id="weekly-heading"
          title="8-day forecast"
          :icon="CalendarDays"
        />
        <p
          v-if="weatherStore.error && weeklyForecast.length"
          role="status"
          class="mb-3 rounded-panel border border-app-borderSubtle bg-app-conditions p-4 text-sm text-text-muted"
        >
          Couldn’t update the forecast. Showing the last available data.
        </p>
        <WeeklyForecast
          :tablet-mode="display.tabletMode"
          :fullscreen-mode="display.fullscreen"
          :wall-mode="wallMode"
          :unit="settingsStore.temperatureUnit"
          :days="weeklyForecast"
          :selected-index="selectedDayIndex"
          :loading="isLoading"
          :error="hasError"
          @day-selected="onDaySelected"
        />
      </section>
    </template>
  </div>
</template>
